import type { Doctor } from '../types/users';
import { processSignatureImage } from '../utils/processSignature';
import { apiService } from './api';

/**
 * Google Meet connection state for the doctor profile banner. Measured live by the backend
 * (a real token refresh), so it's a simple connected / needs-reconnect state — no date countdown.
 */
export interface GoogleMeetStatus {
    connected: boolean;
    needsReconnect: boolean;
    lastConnectedAt: string | null;
}

class DoctorService {

    /** Full DoctorDto for a specific doctor — backend now requires doctorId. */
    async getDoctorForBooking(doctorId: string): Promise<Doctor> {
        return apiService.get<Doctor>('/booking/doctor', {
            params: { doctorId },
        });
    }

    async getDoctorById(id: string): Promise<Doctor | undefined> {
        try {
            return await this.getDoctorForBooking(id);
        } catch {
            return undefined;
        }
    }

    // Get doctor profile
    async getDoctorProfile(_doctorId: string): Promise<Doctor> {
        return apiService.get<Doctor>('/doctor/profile');
    }

    /** Live Google Meet connection state for the profile banner (connected / needs-reconnect). */
    async getGoogleMeetStatus(): Promise<GoogleMeetStatus> {
        return apiService.get<GoogleMeetStatus>('/doctor/google/status');
    }

    // Update doctor profile
    async updateDoctorProfile(_doctorId: string, updates: Partial<Doctor>): Promise<Doctor> {
        return apiService.put<Doctor>('/doctor/profile', updates);
    }

    // Toggle whether the doctor is bookable by patients.
    // Backend strips the `is` prefix on serialization, so the response uses `available`.
    async updateAvailability(available: boolean): Promise<Doctor> {
        return apiService.patch<Doctor>('/doctor/profile/availability', { available });
    }

    // Signature upload (3-step S3 presigned URL flow)
    async initiateSignatureUpload(
        fileName: string,
        contentType: string
    ): Promise<{ uploadUrl: string; s3Key: string }> {
        return apiService.post('/doctor/profile/signature/initiate', { fileName, contentType });
    }

    async confirmSignatureUpload(s3Key: string): Promise<Doctor> {
        return apiService.patch('/doctor/profile/signature/confirm', { s3Key });
    }

    async uploadSignature(file: File): Promise<Doctor> {
        const ALLOWED_TYPES = ['image/png', 'image/jpeg'];
        const MAX_SIZE = 2 * 1024 * 1024; // 2MB

        if (!ALLOWED_TYPES.includes(file.type)) {
            throw new Error('Only PNG or JPEG images are allowed.');
        }
        if (file.size > MAX_SIZE) {
            throw new Error('Signature must be smaller than 2 MB.');
        }

        // Process the photo into a clean transparent PNG (background removed,
        // ink darkened, auto-cropped) so it composites cleanly on white paper.
        const processed = await processSignatureImage(file);

        // Step 1: Initiate
        const { uploadUrl, s3Key } = await this.initiateSignatureUpload(
            processed.name,
            processed.type
        );

        // Step 2: Upload directly to S3 (no auth header, Content-Type must match)
        const s3Response = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': processed.type },
            body: processed,
        });
        if (!s3Response.ok) {
            throw new Error('Failed to upload signature. Please try again.');
        }

        // Step 3: Confirm
        return this.confirmSignatureUpload(s3Key);
    }
}

export const doctorService = new DoctorService();
